import { useCallback, useEffect, useRef, useState } from 'react';

export type ResourceStatus = 'idle' | 'loading' | 'refreshing' | 'success' | 'error';

export interface UseResourceOptions {
  /** Gate fetching until prerequisites (token, org id, permission) exist. While false: 'idle'. */
  enabled?: boolean;
  /** The organization id. When it changes (not on first mount) data is dropped and the state returns to 'loading'. */
  resetKey?: string | number | null;
  /**
   * Inputs the fetcher closes over besides the organization (a template slug).
   * A change fetches again and keeps the current data while it runs.
   */
  deps?: readonly unknown[];
}

export interface UseResourceResult<T> {
  data: T | undefined;
  status: ResourceStatus;
  /** The raw caught value — hand it to ErrorState's `error`, never a humanized string. */
  error: unknown;
  /** Fetches again, keeping `data` while it runs ('refreshing'); resolves the new data, or undefined on failure. */
  refetch: () => Promise<T | undefined>;
}

function isAbort(err: unknown): boolean {
  return err instanceof Error && err.name === 'AbortError';
}

/**
 * One bounded, non-list fetch with designed loading, error and success states:
 * option enums, single records, counts, map data. Never a picker over an
 * unbounded set — those are lists.
 *
 * A response from a superseded request (a newer fetch, an organization change,
 * an unmount) is discarded even when the fetcher ignores its signal, so a slow
 * answer can never overwrite a newer one. Empty copy belongs to 'success' only.
 */
export function useResource<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  { enabled = true, resetKey = null, deps = [] }: UseResourceOptions = {},
): UseResourceResult<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [status, setStatus] = useState<ResourceStatus>(enabled ? 'loading' : 'idle');
  const [error, setError] = useState<unknown>(null);

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const generationRef = useRef(0);
  const controllerRef = useRef<AbortController | null>(null);
  const hasDataRef = useRef(false);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const run = useCallback(async (): Promise<T | undefined> => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const generation = ++generationRef.current;

    setStatus(hasDataRef.current ? 'refreshing' : 'loading');
    setError(null);
    try {
      const result = await fetcherRef.current(controller.signal);
      if (generation !== generationRef.current) return undefined;
      hasDataRef.current = true;
      setData(result);
      setStatus('success');
      return result;
    } catch (err: unknown) {
      if (generation !== generationRef.current || isAbort(err)) return undefined;
      console.error('Resource load failed', err);
      setError(err);
      setStatus('error');
      return undefined;
    }
  }, []);

  const previousResetKey = useRef(resetKey);
  useEffect(() => {
    // Organization change (never first mount): drop what belonged to the
    // previous one before fetching again, so its data is never shown under
    // the new organization.
    if (previousResetKey.current !== resetKey) {
      previousResetKey.current = resetKey;
      hasDataRef.current = false;
      setData(undefined);
      setError(null);
    }
    if (!enabled) {
      generationRef.current += 1;
      controllerRef.current?.abort();
      setStatus(hasDataRef.current ? 'success' : 'idle');
      return;
    }
    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, resetKey, run, ...deps]);

  useEffect(
    () => () => {
      generationRef.current += 1;
      controllerRef.current?.abort();
    },
    [],
  );

  const refetch = useCallback(() => (enabledRef.current ? run() : Promise.resolve(undefined)), [run]);

  return { data, status, error, refetch };
}
